import React, { useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { businessInfo } from '../data/mock';
import { Scale, Mail, Phone } from 'lucide-react';

export const TermsConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-amber-50 py-20">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center bg-emerald-100 text-emerald-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Scale className="h-4 w-4 mr-2" />
            Legal Terms
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-emerald-900 mb-4">
            Terms & Conditions
          </h1>
          <p className="text-gray-600">
            Last Updated: December 2025
          </p>
        </div>

        {/* Content */}
        <Card className="border-2 border-emerald-200 shadow-xl">
          <CardContent className="p-8 md:p-12">
            <div className="prose prose-emerald max-w-none">
              
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Welcome to Claim A Trademark. These Terms and Conditions ("Terms") govern your use of our website and trademark registration services. By accessing or using our services, you agree to be bound by these Terms and our Privacy Policy.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  If you do not agree with any part of these Terms, you must not use our services. We reserve the right to modify these Terms at any time, and your continued use of our services constitutes acceptance of any changes.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">2. Services Overview</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Claim A Trademark provides professional trademark registration services, including but not limited to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Trademark search and analysis</li>
                  <li>Federal trademark application preparation and filing</li>
                  <li>USPTO office action responses</li>
                  <li>Trademark monitoring and enforcement support</li>
                  <li>Trademark renewal services</li>
                  <li>Legal consultation regarding trademark matters</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  Our services are provided by experienced trademark attorneys and legal specialists. However, the use of our services does not create an attorney-client relationship unless explicitly established through a separate written agreement.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">3. Eligibility and Registration</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You must be at least 18 years of age to use our services. By using our services, you represent and warrant that:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>You have the legal capacity to enter into binding agreements</li>
                  <li>All information you provide is accurate, current, and complete</li>
                  <li>You will maintain the accuracy of such information</li>
                  <li>You have the authority to register trademarks on behalf of any business entity</li>
                  <li>Your use of our services complies with all applicable laws and regulations</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">4. Fees and Payment</h2>
                
                <h3 className="text-xl font-semibold text-emerald-800 mb-3">4.1 Service Fees</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our service fees are clearly stated on our pricing page and in any service agreements. Fees are subject to change at any time, but changes will not affect services already purchased.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">4.2 Government Fees</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  USPTO government filing fees are separate from our service fees and are non-refundable. Current government fees range from $250-$350 per class of goods/services. These fees are paid directly to the USPTO and are subject to change by the government.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">4.3 Payment Terms</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Payment is required before services are rendered unless otherwise agreed in writing. We accept major credit cards and other payment methods as indicated on our website. All payments are processed securely through third-party payment processors.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">4.4 Refund Policy</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Service fees may be refunded if requested before work has commenced. Once we have begun work on your trademark application, service fees are non-refundable. Government filing fees paid to the USPTO are never refundable. Each service package may have specific refund terms outlined in your service agreement.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">5. Client Responsibilities</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  As a client, you agree to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Provide accurate and complete information for trademark applications</li>
                  <li>Respond promptly to requests for additional information or documentation</li>
                  <li>Review all documents and correspondence carefully before approval</li>
                  <li>Notify us immediately of any changes to your contact information</li>
                  <li>Use your trademark in commerce as represented in your application</li>
                  <li>Maintain proper specimens showing trademark use</li>
                  <li>Comply with all USPTO requirements and deadlines</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">6. No Guarantee of Registration</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While we strive for the highest success rate, we cannot guarantee that:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Your trademark application will be approved by the USPTO</li>
                  <li>Your trademark will not face opposition from third parties</li>
                  <li>Your trademark search will identify all potential conflicts</li>
                  <li>Registration will be completed within a specific timeframe</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  The USPTO has sole authority over trademark registration decisions. We will use our expertise to maximize your chances of success, but final decisions rest with the USPTO examining attorney and the Trademark Trial and Appeal Board.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">7. Intellectual Property Rights</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All content on our website, including text, graphics, logos, images, and software, is the property of Claim A Trademark or its licensors and is protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You retain all rights to your trademark and related materials. We claim no ownership over your intellectual property. However, by using our services, you grant us a limited license to use your trademark information solely for the purpose of providing our services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">8. Confidentiality</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We treat all client information as confidential and will not disclose it to third parties except:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>As required to provide our services (e.g., filing with the USPTO)</li>
                  <li>With your explicit consent</li>
                  <li>As required by law or legal process</li>
                  <li>To protect our rights or the rights of others</li>
                  <li>To our service providers under confidentiality agreements</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">9. Limitation of Liability</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  To the fullest extent permitted by law, Claim A Trademark and its officers, directors, employees, and agents shall not be liable for:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>Loss of profits, revenue, data, or business opportunities</li>
                  <li>Trademark application rejections or delays by the USPTO</li>
                  <li>Third-party opposition or infringement claims</li>
                  <li>Actions or decisions of the USPTO or other government agencies</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  Our total liability for any claim arising from our services shall not exceed the fees paid by you for the specific service giving rise to the claim.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">10. Indemnification</h2>
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Claim A Trademark and its affiliates from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from: (a) your use of our services, (b) your violation of these Terms, (c) your violation of any third-party rights, or (d) any false or misleading information you provide.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">11. Termination</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We reserve the right to terminate or suspend your access to our services at any time, with or without notice, for any reason, including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Violation of these Terms</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>Non-payment of fees</li>
                  <li>Failure to cooperate or provide required information</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  You may terminate your relationship with us by providing written notice. Termination does not relieve you of any payment obligations for services already rendered.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">12. Disclaimer of Warranties</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our services are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Warranties of merchantability or fitness for a particular purpose</li>
                  <li>Warranties of uninterrupted or error-free service</li>
                  <li>Warranties that our services will meet your specific requirements</li>
                  <li>Warranties regarding the accuracy or completeness of content</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">13. Dispute Resolution</h2>
                
                <h3 className="text-xl font-semibold text-emerald-800 mb-3">13.1 Governing Law</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">13.2 Arbitration</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Any dispute arising from these Terms or our services shall be resolved through binding arbitration in Orange County, California, in accordance with the rules of the American Arbitration Association. You waive any right to a jury trial or to participate in a class action lawsuit.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">13.3 Exceptions</h3>
                <p className="text-gray-700 leading-relaxed">
                  Either party may seek injunctive relief in court for intellectual property infringement or violation of confidentiality obligations.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">14. Miscellaneous</h2>
                
                <h3 className="text-xl font-semibold text-emerald-800 mb-3">14.1 Entire Agreement</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms, together with our Privacy Policy and any service agreements, constitute the entire agreement between you and Claim A Trademark.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">14.2 Severability</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">14.3 Waiver</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.
                </p>

                <h3 className="text-xl font-semibold text-emerald-800 mb-3 mt-6">14.4 Assignment</h3>
                <p className="text-gray-700 leading-relaxed">
                  You may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations without restriction.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-900 mb-4">15. Contact Information</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  For questions about these Terms or our services, please contact us:
                </p>
                
                <div className="bg-emerald-50 rounded-lg p-6 space-y-3">
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-emerald-700 mr-3" />
                    <div>
                      <p className="font-semibold text-emerald-900">Email:</p>
                      <a href={`mailto:${businessInfo.email}`} className="text-emerald-700 hover:text-emerald-800">
                        {businessInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-emerald-700 mr-3" />
                    <div>
                      <p className="font-semibold text-emerald-900">Phone:</p>
                      <a href={`tel:${businessInfo.phone}`} className="text-emerald-700 hover:text-emerald-800">
                        {businessInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <Scale className="h-5 w-5 text-emerald-700 mr-3 mt-1" />
                    <div>
                      <p className="font-semibold text-emerald-900">Address:</p>
                      <p className="text-gray-700">{businessInfo.address}</p>
                    </div>
                  </div>
                </div>
              </section>

              <div className="bg-amber-50 border-l-4 border-amber-600 p-6 rounded-r-lg mt-8">
                <p className="text-gray-700 leading-relaxed">
                  <strong className="text-amber-900">Important Notice:</strong> By using Claim A Trademark's services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree with these Terms, you must discontinue use of our services immediately.
                </p>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Back to Home Link */}
        <div className="text-center mt-8">
          <a
            href="/"
            className="inline-block bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
};
